/** Клиент API. Одна точка, где живёт заголовок подписи и разбор отказов.
 *
 * Отказы — не «ошибка», а состояния экрана: подпись протухла, мест нет, сети нет.
 * У каждого свой экран (FlowEdge), поэтому различаем их здесь, а не в компонентах.
 */
import { initData } from "./telegram.ts";

export type Failure =
  | "expired" | "no-seat" | "offline" | "broken" | "draft-lost" | "gone" | "unclear"
  | "taken" | "too-long";

export class ApiError extends Error {
  constructor(readonly kind: Failure) {
    super(kind);
  }
}

export type Me = {
  telegram_id: number;
  base_currency: string;
  default_currency: string;
  monthly_allowance: string | null;
  proactive_enabled: boolean;
  is_owner: boolean;
};

export type Allowance = {
  spent: string;
  limit: string;
  left: string;
  over: string;
  days_left: number;
  is_over: boolean;
  base_currency: string;
  /** С какого дня действует лимит. `null` — задан с начала месяца. */
  since: string | null;
};

export type MonthReview = {
  spent: string;
  limit: string;
  saved: string;
  overspent: string;
  /** `null` — двигать лимит нечего: промахов нет или они разнонаправленные. */
  proposal: string | null;
  streak: number;
  savings_delta: string;
  base_currency: string;
  /** Первое число закрытого месяца. */
  month: string;
  /** Идёт ли ещё первая неделя нового месяца. Считает сервер: часовой пояс
   *  продукта живёт там, а не в браузере. */
  is_fresh: boolean;
};

export type Operation = {
  id: number;
  op_type: "expense" | "income" | "refund";
  amount: string;
  currency: string;
  amount_base: string;
  base_currency: string;
  category_id: number;
  category_name: string;
  description: string;
  occurred_at: string;
  /** День курса пересчёта. `null` — платил в базовой валюте, пересчёта не было. */
  fx_date: string | null;
};

export type CategorySlice = {
  name: string;
  amount: string;
  share: number;
  /** Цвет, выбранный для категории. `null` — красим по рангу трат. */
  color: string | null;
  /** Ключ значка — он рисуется внутри дольки пирога. */
  icon: string | null;
  /** id для фильтра ленты на сервере. `null` — склеенный хвост «Остальное». */
  category_id: number | null;
};

export type Feed = { items: Operation[]; next: string | null };

/** Какую сторону денег разбираем. Не «фильтр», а предмет разговора: у трат и
 *  доходов свои категории, свои суммы и свой смысл слова «100%». */
export type Side = "expense" | "income";

export type Budget = {
  category_id: number;
  category_name: string;
  spent: string;
  limit: string;
  status: "ok" | "near" | "over";
  base_currency: string;
};

export type Budgets = { items: Budget[]; base_currency: string };

export type Summary = {
  kind: string;
  start: string;
  end: string;
  base_currency: string;
  expense_total: string;
  income_total: string;
  net: string;
  categories: CategorySlice[];
  daily: { day: string; expense: string; income: string }[];
  tx_count: number;
  /** Состав доходов — своим списком: складывать зарплату с едой нельзя. */
  income_categories: CategorySlice[];
  /** Записей с каждой стороны. `tx_count` — общий, и подпись под заголовком
   *  «Траты» на нём врала: он считал и зарплаты. */
  expense_count: number;
  income_count: number;
};

export type RecordKind =
  | "recorded" | "needs_currency" | "needs_confirm" | "transfer_ignored"
  | "subscription" | "budget_set" | "fx_set" | "fx_rejected" | "base_changed"
  | "capital_taken"
  | "failed" | "unclear" | "unsupported";

export type RecordOut = {
  kind: RecordKind;
  hint: string;
  transcript: string;
  operations: Operation[];
  allowance: Allowance | null;
  currencies: string[];
  amount: string | null;
  currency: string;
  amount_base: string | null;
  base_currency: string;
  title: string;
};

export type OperationPatch = {
  amount?: string;
  currency?: string;
  op_type?: Operation["op_type"];
  category?: string;
  description?: string;
  occurred_on?: string;
};

export type CategoryOut = { id: number; name: string; kind: string };

export type Pot = {
  name: string;
  amount: string;
  currency: string;
  amount_base: string;
  delta: string | null;   // null — кошелёк новый, сравнивать не с чем
};

export type Capital = {
  taken_on: string;
  base_currency: string;
  total: string;
  pots: Pot[];
  previous_on: string | null;
  previous_total: string | null;
  delta: string | null;   // считает домен: вычитание денег во фронте — это float
  fx_effect: string;
};

export type CapitalPoint = { day: string; total: string };

export type Subscription = {
  id: number;
  name: string;
  amount: string;
  currency: string;
  period: "monthly" | "yearly";
  period_label: string;
  monthly_amount: string;
  category_name: string;
  next_due: string;
  active: boolean;
  /** Бот пишет трату сам в день списания; выключено — ждёт отметки руками. */
  auto_charge: boolean;
};

export type Subscriptions = {
  active: Subscription[];
  paused: Subscription[];
  monthly_total: string;
  base_currency: string;
};

export type SubscriptionPatch = {
  name?: string;
  amount?: string;
  period?: Subscription["period"];
  next_due?: string;
  active?: boolean;
  auto_charge?: boolean;
};

export type MonthRate = {
  year: number; month: number; income: string; expense: string; rate: string;
};

export type Bucket = { bucket: string; amount: string; share: string; target: string };

export type Savings = {
  base_currency: string;
  window_months: number;
  months: MonthRate[];
  rate_window: string;
  buffer: string;
  buffer_months: string;
  buffer_target_months: number;
  baseline_monthly: string;
  baseline_is_default: boolean;
  spent_mtd: string;
  burn_per_day: string;
  forecast_month: string;
  days_elapsed: number;
  days_in_month: number;
  buckets: Bucket[];
  coverage: string;
  unclassified: string;
};

export type AppSettings = {
  base_currency: string;
  default_currency: string;
  proactive_enabled: boolean;
  reminder_hour: number;
  reminder_hour_is_personal: boolean;
  /** Палитра картинок, которые бот шлёт в чат. */
  palette: string;
  /** Месячный лимит. `null` — не задан. */
  monthly_allowance: string | null;
  /** Число месяца для снимка капитала. `null` — последний день месяца. */
  snapshot_day: number | null;
  is_owner: boolean;
};

export type SettingsPatch = {
  base_currency?: string;
  default_currency?: string;
  proactive_enabled?: boolean;
  reminder_hour?: number;
  reset_reminder_hour?: boolean;
  snapshot_day?: number;
  reset_snapshot_day?: boolean;
  palette?: string;
};

export type Rate = {
  quote: string; rate: string; manual: boolean; updated_at: string | null;
};

export type Rates = { base_currency: string; rates: Rate[]; can_edit: boolean };

export type CategoryFull = {
  id: number;
  name: string;
  kind: string;
  bucket: string | null;   // null — «не размечено», законное состояние
  is_seed: boolean;
  /** Выбранные цвет и значок. `null` — не выбраны. */
  color: string | null;
  icon: string | null;
};

export type CategoryDeleted = { moved: number; into_id: number; into_name: string };

export type CategoryNew = {
  kind: "expense" | "income";
  name: string;
  /** Тип только у расходов: у дохода «нужное / хотелки» не бывает. */
  bucket?: string;
};

export type CategoryPatch = {
  name?: string;
  bucket?: string;
  clear_bucket?: boolean;
  /** Пустая строка — сброс к автоматике, отсутствие поля — «не трогать». */
  color?: string;
  icon?: string;
};

/** Свой диапазон: календарные дни, обе границы включительно. Момент собирает
 *  сервер — часовой пояс это свойство продукта, а не браузера. */
export type Span = { from: string; to: string };

function spanParams(span?: Span): Record<string, string> {
  return span ? { date_from: span.from, date_to: span.to } : {};
}

export type Pin = { place: string; category_id: number; category_name: string };

export type BugReport = { id: number; note: string; created_at: string; resolved: boolean };

/** Год — ради разбора: «куда ушёл год» месяц не отвечает, а свой диапазон
 *  отвечает неудобно (MA-FR-6). */
export type Period = "day" | "week" | "month" | "year";

/** Сырые байты записи, без multipart: ради одного файла лишняя зависимость на
 *  сервере не нужна, а тип содержимого нужен Whisper для определения формата. */
async function sendBlob<T>(path: string, blob: Blob): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method: "POST",
      headers: {
        "X-Telegram-Init-Data": initData(),
        "Content-Type": blob.type || "audio/webm",
      },
      body: blob,
    });
  } catch {
    throw new ApiError("offline");
  }
  if (res.status === 401) throw new ApiError("expired");
  if (res.status === 403) throw new ApiError("no-seat");
  if (res.status === 413) throw new ApiError("too-long");
  if (!res.ok) throw new ApiError("broken");
  return (await res.json()) as T;
}

async function send<T>(path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method: "POST",
      headers: { "X-Telegram-Init-Data": initData(), "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError("offline");
  }
  if (res.status === 401) throw new ApiError("expired");
  if (res.status === 403) throw new ApiError("no-seat");
  // 409 — черновик потерян: сервер перезапустился между вопросом и ответом.
  if (res.status === 409) throw new ApiError("draft-lost");
  if (!res.ok) throw new ApiError("broken");
  return (await res.json()) as T;
}

async function mutate<T>(path: string, method: "POST" | "PATCH" | "PUT" | "DELETE",
                         body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method,
      headers: { "X-Telegram-Init-Data": initData(), "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError("offline");
  }
  if (res.status === 401) throw new ApiError("expired");
  if (res.status === 403) throw new ApiError("no-seat");
  if (res.status === 404) throw new ApiError("gone");
  // 409 в правках — имя занято; в записи — потерянный черновик.
  if (res.status === 409) throw new ApiError("taken");
  // 422 — сервер не понял сумму: это вопрос к человеку, а не поломка.
  if (res.status === 422) throw new ApiError("unclear");
  if (!res.ok) throw new ApiError("broken");
  // 204 — тела нет. `res.json()` на пустом теле бросает, и удаление выглядело бы
  // сломанным ровно в тот момент, когда оно сработало.
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

async function get<T>(path: string): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, { headers: { "X-Telegram-Init-Data": initData() } });
  } catch {
    // Сеть не ответила. Показывать устаревшие цифры нельзя — по ним принимают решения.
    throw new ApiError("offline");
  }
  if (res.status === 401) throw new ApiError("expired");
  if (res.status === 403) throw new ApiError("no-seat");
  if (!res.ok) throw new ApiError("broken");
  return (await res.json()) as T;
}

/** Находка: что человек пропустил, а данные показывают.
 *
 *  Поля разбираются по `kind` и часть пустует по назначению: у пропущенного
 *  списания нет `days_quiet`, у провала в записях нет подписки.
 */
export type Finding = {
  kind: "missed_charge" | "duplicate" | "silent";
  subscription_id: number | null;
  due_on: string | null;
  days_late: number | null;
  keep_id: number | null;
  drop_id: number | null;
  name: string | null;
  amount: string | null;
  currency: string | null;
  days_quiet: number | null;
  last_record_on: string | null;
};

export type Findings = { items: Finding[] };

/** Строка расхода на модель: на что и сколько раз её звали. */
export type LlmSpendLine = { purpose: string; calls: number; amount: string };

/** Бюджет на модель за календарный месяц.
 *
 *  `low` — осталось меньше доллара: экран предупреждает заранее, потому что
 *  упереться в отказ посреди разговора хуже, чем знать о границе.
 */
export type LlmSpend = {
  spent: string;
  budget: string;
  remaining: string;
  low: boolean;
  lines: LlmSpendLine[];
};

export const api = {
  me: () => get<Me>("/me"),
  /** `null` = лимит не задан. Не нули: «осталось 0 $» — враньё (§16.22). */
  allowance: () => get<Allowance | null>("/allowance"),
  monthReview: () => get<MonthReview | null>("/month-review"),
  setAllowance: (amount: string, currency: string) =>
    mutate<Allowance>("/allowance", "PUT", { amount, currency }),
  summary: (period: Period, span?: Span) =>
    get<Summary>(`/summary?${new URLSearchParams({ period, ...spanParams(span) })}`),
  operations: (period: Period, filters: {
    categoryId?: number | null; q?: string; span?: Span; cursor?: string | null;
    limit?: number; side?: Side;
  } = {}) => {
    const params = new URLSearchParams({ period, ...spanParams(filters.span) });
    if (filters.categoryId != null) params.set("category_id", String(filters.categoryId));
    if (filters.q) params.set("q", filters.q);
    if (filters.side) params.set("side", filters.side);
    if (filters.cursor) params.set("cursor", filters.cursor);
    if (filters.limit) params.set("limit", String(filters.limit));
    return get<Feed>(`/operations?${params}`);
  },
  record: (text: string) => send<RecordOut>("/record", { text }),
  recordCurrency: (currency: string) => send<RecordOut>("/record/currency", { currency }),
  recordConfirm: () => send<RecordOut>("/record/confirm"),
  recordVoice: (audio: Blob) => sendBlob<RecordOut>("/record/voice", audio),
  operation: (id: number) => get<Operation>(`/operations/${id}`),
  patchOperation: (id: number, patch: OperationPatch) =>
    mutate<Operation>(`/operations/${id}`, "PATCH", patch),
  deleteOperation: (id: number) => mutate<Operation>(`/operations/${id}`, "DELETE"),
  restoreOperation: (id: number) => send<Operation>(`/operations/${id}/restore`),
  /** Повтор траты сегодняшним днём: новая запись, прошлая остаётся фактом своего дня. */
  repeatOperation: (id: number) => send<Operation>(`/operations/${id}/repeat`),
  budgets: () => get<Budgets>("/budgets"),
  setBudget: (category_id: number, amount: string, currency: string) =>
    mutate<Budget>("/budgets", "PUT", { category_id, amount, currency }),
  dropBudget: (category_id: number) =>
    mutate<void>(`/budgets/${category_id}`, "DELETE"),
  categories: (kind: "expense" | "income" = "expense") =>
    get<CategoryOut[]>(`/categories?kind=${kind}`),
  /** `null` — снимков ещё нет. Не нули: «капитал 0» соврал бы. */
  capital: () => get<Capital | null>("/capital"),
  capitalHistory: () => get<CapitalPoint[]>("/capital/history"),
  snapshot: (text: string) => send<RecordOut>("/capital/snapshot", { text }),
  snapshotPots: (pots: { name: string; amount: string; currency: string }[]) =>
    send<RecordOut>("/capital/snapshot/pots", { pots }),
  renamePot: (name: string, next: string) =>
    mutate<Capital>(`/capital/pots/${encodeURIComponent(name)}`, "PATCH", { name: next }),
  dropPot: (name: string) =>
    mutate<Capital>(`/capital/pots/${encodeURIComponent(name)}`, "DELETE"),
  /** Находки по убыванию важности. Ищутся запросом к базе, модель не зовётся —
   *  запрос бесплатный, и дёргать его при каждом открытии не жалко. */
  findings: () => get<Findings>("/findings"),
  llmSpend: () => get<LlmSpend>("/llm-spend"),
  subscriptions: () => get<Subscriptions>("/subscriptions"),
  addSubscription: (body: {
    name: string; amount: string; currency: string;
    period: "monthly" | "yearly"; next_due: string;
  }) => send<Subscription>("/subscriptions", body),
  patchSubscription: (id: number, patch: SubscriptionPatch) =>
    mutate<Subscription>(`/subscriptions/${id}`, "PATCH", patch),
  /** `on` — день, когда деньги ушли: срок 13-го, а заплатил первого. */
  paySubscription: (id: number, on?: string) =>
    send<RecordOut>(`/subscriptions/${id}/pay`, on ? { on } : undefined),
  deleteSubscription: (id: number) => mutate<{ deleted: boolean }>(`/subscriptions/${id}`, "DELETE"),
  savings: () => get<Savings>("/savings"),
  settings: () => get<AppSettings>("/settings"),
  patchSettings: (patch: SettingsPatch) => mutate<AppSettings>("/settings", "PATCH", patch),
  rates: () => get<Rates>("/rates"),
  clearRate: (quote: string) => mutate<Rates>(`/rates/${quote}`, "DELETE"),
  allCategories: (kind: "expense" | "income" = "expense") =>
    get<CategoryFull[]>(`/categories/all?kind=${kind}`),
  deleteCategory: (id: number, intoId: number) =>
    mutate<CategoryDeleted>(`/categories/${id}?into_id=${intoId}`, "DELETE"),
  createCategory: (body: CategoryNew) =>
    mutate<CategoryFull>("/categories", "POST", body),
  patchCategory: (id: number, patch: CategoryPatch) =>
    mutate<CategoryFull>(`/categories/${id}`, "PATCH", patch),
  pins: () => get<Pin[]>("/pins"),
  forgetPin: (place: string) =>
    mutate<{ forgotten: boolean }>(`/pins/${encodeURIComponent(place)}`, "DELETE"),
  bugs: () => get<BugReport[]>("/bugs"),
  reportBug: (note: string) => send<BugReport[]>("/bugs", { note }),
  /** Ссылка на CSV. Диапазон перекрывает период — как и в разборе. */
  exportUrl: (period: Period, span?: Span) => {
    const params = new URLSearchParams({ period, ...spanParams(span) });
    return `/api/export?${params}`;
  },
};
